import { CSSProperties } from 'react';
import { useState, useEffect } from 'react';

export type Predicate<T> = (element: T) => boolean;

export type UseStateCallback<T> = (action: T | ((prev: T) => T)) => void;
export type UseState<T> = [T, UseStateCallback<T>];

/**
 * Async typesafe version of useMemo
 */
export function useAsyncMemo<T, S = undefined>(
  fn: () => Promise<T>,
  params: any[],
  orDefault?: S,
): [S] extends [undefined] ? T | undefined : T | S {
  const [data, setData] = useState<T | S | undefined>(orDefault);
  useEffect(() => {
    fn()
      .then((it) => setData(it ?? orDefault))
      .catch(() => setData(orDefault));
  }, params);

  //@ts-expect-error
  return data;
}

/**
 * calls `block` with `elem` iff `elem` is not null or undefined and returns the result
 */
export function using<T, S>(elem: T | undefined | null, block: (t: T) => S): S | undefined {
  if (elem === undefined || elem === null) {
    return;
  }
  return block(elem);
}

/**
 * return a list of objects
 */
export function multiple<T>(elem?: T | T[]): T[] {
  if (Array.isArray(elem)) {
    return elem;
  }
  if (elem === undefined) {
    return [];
  }
  return [elem];
}

export type FilterKeys<T, F> = keyof {
  [K in keyof T as T[K] extends F ? K : never]: T[K];
};

export type CSSVar = `--${string}`;

export type CSSPropertiesWithVars = CSSProperties & Record<CSSVar, string | number>;
